/**
 * StripePaymentElement — inline Stripe Payment Element checkout.
 *
 * Renders the Payment Element (Stripe Elements) directly in the page —
 * no iframe, no modal. Supports all payment methods enabled on the
 * account (card + Apple Pay + Link + iDEAL + Bepaal + SEPA), driven by
 * `automatic_payment_methods` on the server-side PaymentIntent.
 *
 * Flow:
 *   1. Fetch client_secret + publishable_key from
 *      POST /api/listings/{slug}/payment-intent
 *   2. loadStripe(publishable_key) → wrap in <Elements> with clientSecret
 *   3. Render <PaymentElement /> + a custom submit button
 *   4. On submit call stripe.confirmPayment({ elements, confirmParams }).
 *      For card payments this stays on-page (returns { paymentIntent })
 *      and we jump straight to success. For wallet flows (iDEAL etc.)
 *      Stripe redirects to return_url with ?payment_intent=<pi_xxx>
 *      &payment_intent_client_secret=<...>&redirect_status=<succeeded|
 *      requires_payment_method|processing> — the Lander picks this up
 *      via its existing return-URL polling.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api } from "@/lib/api";

export default function StripePaymentElement({ slug, buyerEmail = null, discountCode = null, onDiscountRejected, onClose }) {
  const [clientSecret, setClientSecret] = useState("");
  const [publishableKey, setPublishableKey] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const onRejectedRef = useRef(onDiscountRejected);
  onRejectedRef.current = onDiscountRejected;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.post(`/listings/${slug}/payment-intent`, { origin_url: window.location.origin, buyer_email: buyerEmail, discount_code: discountCode })
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.client_secret || !r.data?.publishable_key) {
          setError("Stripe is not fully configured on this listing.");
          return;
        }
        if (r.data.discount_rejected) onRejectedRef.current?.();
        setClientSecret(r.data.client_secret);
        setPublishableKey(r.data.publishable_key);
        setPaymentIntentId(r.data.payment_intent_id);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not start checkout — please retry.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, buyerEmail, discountCode]);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  if (loading) {
    return <PaymentSkeleton />;
  }
  if (error) {
    return (
      <div data-testid="payment-element-error" style={{ padding: "18px 22px", border: "1px solid #FEE2E2", background: "#FEF2F2", color: "#991B1B", borderRadius: 12, fontSize: 14 }}>
        {error}
      </div>
    );
  }
  if (!clientSecret || !stripePromise) return null;

  return (
    <div data-testid="payment-element-wrap">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#03142f",
              colorText: "#03142f",
              colorBackground: "#ffffff",
              colorDanger: "#B42318",
              fontFamily: "var(--cd-font), Inter, system-ui, sans-serif",
              fontSizeBase: "15px",
              borderRadius: "10px",
              spacingUnit: "4px",
            },
            rules: {
              ".Input": {
                border: "1px solid #E4E7EC",
                boxShadow: "none",
                padding: "12px 14px",
              },
              ".Input:focus": {
                border: "1px solid #4b6fff",
                boxShadow: "0 0 0 4px rgba(75,111,255,.15)",
              },
              ".Label": {
                fontWeight: "700",
                fontSize: "13px",
                color: "#475467",
              },
              ".Tab": { border: "1px solid #E4E7EC", padding: "10px 12px" },
              ".Tab--selected": { border: "1px solid #03142f", boxShadow: "0 0 0 2px rgba(3,20,47,.06)" },
            },
          },
          loader: "always",
        }}
      >
        <CheckoutForm paymentIntentId={paymentIntentId} slug={slug} onClose={onClose} />
      </Elements>
    </div>
  );
}

function CheckoutForm({ paymentIntentId, slug, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setMsg("");

    // Return URL — Stripe redirects here for wallet flows (iDEAL, Bepaal,
    // SEPA). Cards succeed inline without a redirect. We include the
    // payment_intent id in the URL so the lander's return-handler knows
    // which transaction to poll on.
    const returnUrl = `${window.location.origin}/l/${slug}?pi=${paymentIntentId}`;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // Card flow stays on this page; we only redirect when the payment
      // method itself needs a hosted step (iDEAL/Bepaal/SEPA/3DS OTP).
      redirect: "if_required",
    });

    if (error) {
      setMsg(error.message || "Payment failed. Please try again.");
      setSubmitting(false);
      toast.error(error.message || "Payment failed");
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Card path — no redirect happened. Jump the URL bar so the
      // Lander's return-URL polling kicks in and closes the loop.
      toast.success("Payment received. Confirming...");
      window.location.replace(`${window.location.pathname}?pi=${paymentIntent.id}`);
      return;
    }
    // requires_action / processing — Stripe will handle the next step
    setSubmitting(false);
    setMsg("Payment is still processing. You'll get an email once it clears.");
  };

  return (
    <form onSubmit={submit} data-testid="payment-element-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="payment-element-submit"
        style={{
          marginTop: 22,
          width: "100%",
          padding: "14px 18px",
          borderRadius: 12,
          border: 0,
          background: submitting ? "#475467" : "var(--cd-navy-950)",
          color: "#fff",
          fontFamily: "var(--cd-font)",
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: "-0.01em",
          cursor: submitting ? "not-allowed" : "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          transition: "background .18s ease",
        }}
      >
        <Lock size={14} strokeWidth={2.4} />
        {submitting ? "Processing…" : "Pay securely"}
      </button>
      {msg && (
        <div data-testid="payment-element-msg" style={{ marginTop: 12, fontSize: 13, color: "#475467" }}>
          {msg}
        </div>
      )}
      <p style={{ marginTop: 14, fontSize: 12, color: "#98A2B3", textAlign: "center" }}>
        Secured by Stripe. Cards, Apple Pay, Link and iDEAL supported.
      </p>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          style={{ marginTop: 8, width: "100%", background: "transparent", border: 0, color: "#98A2B3", fontSize: 12, cursor: "pointer" }}
        >
          Cancel
        </button>
      )}
    </form>
  );
}

function PaymentSkeleton() {
  const bar = (h = 44) => ({
    height: h, background: "#F2F4F7", borderRadius: 10, marginBottom: 10,
  });
  return (
    <div data-testid="payment-element-skeleton" aria-hidden>
      <div style={bar(22)} />
      <div style={bar()} />
      <div style={bar()} />
      <div style={bar()} />
      <div style={{ ...bar(48), marginTop: 18, background: "#E4E7EC" }} />
    </div>
  );
}
